import { useDroppable } from '@dnd-kit/core'
import { Box, Paper, Typography, Chip, Skeleton, Stack } from '@mui/material'
import { Client } from '../../services/api'
import ClientCard from './ClientCard'

interface ClientPipelineColumnProps {
  stageId: string
  label: string
  color?: string
  /** Shown under the heading; absent for the catch-all column. */
  teamLabel?: string
  clients: Client[]
  loading?: boolean
  onClientClick: (client: Client) => void
  onAssignTeam?: (client: Client) => void
  onSendSms?: (client: Client) => void
  onAddNote?: (client: Client) => void
  /** Copy for the placeholder when the column holds nobody. */
  emptyText?: string
}

export default function ClientPipelineColumn({
  stageId,
  label,
  color,
  teamLabel,
  clients,
  loading = false,
  onClientClick,
  onAssignTeam,
  onSendSms,
  onAddNote,
  emptyText = 'No clients in this stage',
}: ClientPipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stageId })

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        width: { xs: '85vw', sm: 288 },
        minWidth: { xs: '85vw', sm: 288 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: isOver ? 'action.hover' : 'background.paper',
        transition: 'background-color 0.2s',
        scrollSnapAlign: { xs: 'start', sm: 'unset' },
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {color && (
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {clients.length}
          </Typography>
        </Stack>
        {teamLabel && (
          <Chip
            label={teamLabel}
            size="small"
            variant="outlined"
            sx={{ mt: 0.75, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          // Cards rather than a spinner, so the column does not collapse and
          // then jump to its real height when the list arrives.
          [0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={76} sx={{ mb: 1 }} />
          ))
        ) : (
          <>
            {clients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onClick={() => onClientClick(client)}
                onAssignTeam={onAssignTeam}
                onSendSms={onSendSms}
                onAddNote={onAddNote}
              />
            ))}

            {clients.length === 0 && (
              // Still a drop target: an empty stage is exactly where someone
              // is about to drag their first client.
              <Box
                sx={{
                  minHeight: 96,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 2,
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {emptyText}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  )
}
